'use client';

/* ──────────────────────────────────────────────
 *  PrestationConfigurator — corps configurable partagé
 *  d'une carte prestation : variantes de prix/durée +
 *  suppléments + quantité + bouton d'action. Utilisé par
 *  le tunnel de réservation (ServiceCard) ET le tunnel
 *  d'achat de carte cadeau (PurchaseTunnel).
 *  Les parties spécifiques au booking (taille de groupe,
 *  bandeau d'indisponibilité) sont injectées via des slots.
 * ────────────────────────────────────────────── */

import type { ReactNode } from 'react';
import { resolveSupplementQuantityMax } from '@cfixe-monorepo/shared';
import type { PriceOption } from '../../types';
import { DurationSelector } from './DurationSelector';
import { SupplementPicker, type SupplementPickerItem } from './SupplementPicker';
import { QuantitySelector } from './QuantitySelector';

export interface PrestationConfiguratorLabels {
  duration_label: string;
  /** Libellé porté par une formule sans créneau (grisée, jamais retirée). */
  duration_unavailable: string;
  price_from: string;
  on_quote: string;
  free: string;
  supplements_label: string;
  add_supplement: string;
  remove_supplement: string;
  supplements_show_more_one: string;
  supplements_show_more_other: string;
  quantity_label: string;
  decrease_quantity: string;
  increase_quantity: string;
}

interface PrestationConfiguratorProps<T extends SupplementPickerItem> {
  prices: PriceOption[];
  selectedPrice: PriceOption;
  onPriceChange: (price: PriceOption) => void;
  supplements: T[];
  onSupplementsChange: (supplements: T[]) => void;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  showQuantity?: boolean;
  maxQuantity?: number;
  labels: PrestationConfiguratorLabels;
  formatPrice: (price: number) => string;
  actionLabel: string;
  onAction: () => void;
  actionDisabled?: boolean;
  /** Pas de bouton d'action (carte rouverte pour sa seule description). */
  hideAction?: boolean;
  /** Slot rendu entre les suppléments et la quantité (ex. taille de groupe côté booking). */
  beforeQuantity?: ReactNode;
  /** Slot rendu entre la quantité et le bouton (ex. bandeau « indisponible » côté booking). */
  afterControls?: ReactNode;
  /**
   * Unités facturées de la ligne (quantité × personnes) : quantité proposée par défaut pour chaque
   * supplément qui se compte au choix. Absent : pas de stepper sur les suppléments.
   */
  supplementDefaultQuantity?: number;
}

export function PrestationConfigurator<T extends SupplementPickerItem>({
  prices,
  selectedPrice,
  onPriceChange,
  supplements,
  onSupplementsChange,
  quantity,
  onQuantityChange,
  showQuantity = true,
  maxQuantity,
  labels,
  formatPrice,
  actionLabel,
  onAction,
  actionDisabled = false,
  hideAction = false,
  beforeQuantity,
  afterControls,
  supplementDefaultQuantity,
}: PrestationConfiguratorProps<T>) {
  return (
    <>
      <DurationSelector
        prices={prices}
        selectedPrice={selectedPrice}
        onChange={onPriceChange}
        formatPrice={formatPrice}
        labels={{
          duration_label: labels.duration_label,
          price_from: labels.price_from,
          on_quote: labels.on_quote,
          free: labels.free,
          unavailable: labels.duration_unavailable,
        }}
      />

      <SupplementPicker
        supplements={supplements}
        onChange={onSupplementsChange}
        formatPrice={formatPrice}
        labels={{
          supplements_label: labels.supplements_label,
          add_supplement: labels.add_supplement,
          remove_supplement: labels.remove_supplement,
          show_more_one: labels.supplements_show_more_one,
          show_more_other: labels.supplements_show_more_other,
        }}
        quantity={
          supplementDefaultQuantity !== undefined
            ? {
                defaultQuantity: supplementDefaultQuantity,
                max: resolveSupplementQuantityMax({ quantity: supplementDefaultQuantity }),
                labels: { decrease: labels.decrease_quantity, increase: labels.increase_quantity },
              }
            : undefined
        }
      />

      {beforeQuantity}

      {showQuantity && (
        <QuantitySelector
          quantity={quantity}
          onChange={onQuantityChange}
          max={maxQuantity}
          labels={{
            quantity_label: labels.quantity_label,
            decrease_quantity: labels.decrease_quantity,
            increase_quantity: labels.increase_quantity,
          }}
        />
      )}

      {afterControls}

      {!hideAction && (
        <button type="button" className="bt-cta" onClick={onAction} disabled={actionDisabled}>
          {actionLabel}
        </button>
      )}
    </>
  );
}
